import {
  Component,
  ElementRef,
  HostListener,
  Input,
  Output,
  ViewChild,
  EventEmitter,
  ViewContainerRef,
  Type,
  ComponentRef,
  AfterViewInit,
  OnDestroy
} from '@angular/core';
import { SafeHtml, SafeResourceUrl, DomSanitizer} from '@angular/platform-browser';
import { CommonModule } from '@angular/common';
import { DragDropModule, CdkDrag } from '@angular/cdk/drag-drop';

@Component({
  selector: 'app-rms-popup',
  templateUrl: './rms-popup.component.html',
  styleUrls: ['./rms-popup.component.scss'],
  imports: [CommonModule, CdkDrag, DragDropModule],
  standalone: true
})
export class RmsPopupComponent implements AfterViewInit, OnDestroy {
  @Input() title: string = 'Popup';
  @Input() reportUrl?: SafeResourceUrl;
  @Input() component?: Type<any>;
  @Input() customHtml?: SafeHtml;
  @Input() data?: any;
  @Input() startingWidth?: string;
  @Input() startingHeight?: string;
  @Input() fileUrl?: string;
  @Output() closeEvent = new EventEmitter<void>();

  @ViewChild('contentContainer', { read: ViewContainerRef }) contentContainer!: ViewContainerRef;
  @ViewChild('popup') popup!: ElementRef;
  @ViewChild('boundingBox') boundingBox!: ElementRef;

  public componentRef?: ComponentRef<any>;
  safeFileUrl?: SafeResourceUrl;
  resizing = false;
  lastX = 0;
  lastY = 0;
  dragPosition = { x: 0, y: 0 };

  constructor(private sanitizer: DomSanitizer) { }

  ngAfterViewInit(): void {
    this.loadContent();
  }

  async loadContent() {
    if (this.component) {
      this.contentContainer.clear();
      this.componentRef = this.contentContainer.createComponent(this.component);
      if (this.data && this.componentRef?.instance) {
        Object.assign(this.componentRef.instance, this.data);
      }
    }

    if (this.fileUrl) {
      this.safeFileUrl =
        this.sanitizer.bypassSecurityTrustResourceUrl(this.fileUrl);
    }

    // Wait for rendering
    await this.waitForRender();
    this.adjustPopupSize();
  }

  private waitForRender(): Promise<void> {
    return new Promise(resolve => requestAnimationFrame(() => resolve()));
  }

  private adjustPopupSize() {
    const popupEl = this.popup?.nativeElement as HTMLElement;
    const boundingBoxEl = this.boundingBox?.nativeElement as HTMLElement;
    if (!popupEl || !boundingBoxEl) return;

    // Apply initial width/height
    if (this.startingWidth) {
      popupEl.style.width = this.startingWidth;
      boundingBoxEl.style.width = this.startingWidth;
    }
    if (this.startingHeight) {
      popupEl.style.height = this.startingHeight;
      boundingBoxEl.style.height = this.startingHeight;
    }

    // Center popup
    const popupRect = popupEl.getBoundingClientRect();
    const boxRect = boundingBoxEl.getBoundingClientRect();
    const x = Math.max(0, (boxRect.width - popupRect.width) / 2);
    const y = Math.max(20, (boxRect.height - popupRect.height) / 2);

    this.dragPosition = { x, y };
    popupEl.style.transform = `translate(${x}px, ${y}px)`;
  }

  onIframeLoad() {
    requestAnimationFrame(() => this.adjustPopupSize());
  }

  closePopup() {
    if (this.componentRef) {
      this.componentRef.destroy();
    }
    this.closeEvent.emit();
  }

  startResize(event: MouseEvent) {
    this.resizing = true;
    this.lastX = event.clientX;
    this.lastY = event.clientY;
    event.preventDefault();
  }

  @HostListener('document:mousemove', ['$event'])
  onResize(event: MouseEvent) {
    if (!this.resizing || !this.popup) return;

    const popupEl = this.popup.nativeElement as HTMLElement;
    const dx = event.clientX - this.lastX;
    const dy = event.clientY - this.lastY;

    popupEl.style.width = `${popupEl.offsetWidth + dx}px`;
    popupEl.style.height = `${popupEl.offsetHeight + dy}px`;

    this.lastX = event.clientX;
    this.lastY = event.clientY;
  }

  @HostListener('document:mouseup')
  stopResize() {
    this.resizing = false;
  }

  ngOnDestroy(): void {
    if (this.componentRef) {
      this.componentRef.destroy();
    }
  }
}
